import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { CreditCard, Headset, Loader2, Building2, Users, Bell, ClipboardList } from "lucide-react";


export function AdminPlans() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/plans").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  return (
    <div data-testid="admin-plans-page">
      <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
        <CreditCard className="w-5 h-5" /> Plans
      </h1>
      <p className="text-sm text-slate-600 mt-2">
        Current business plans, prices, and how many companies are on each one. To assign or change a business's plan,
        go to <Link to="/admin/businesses" className="underline">Businesses</Link> → click Edit on a row.
      </p>
      {data.companies_without_plan > 0 && (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {data.companies_without_plan} legacy company {data.companies_without_plan === 1 ? "is" : "are"} missing a plan_type.
        </div>
      )}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {data.plans.map((p) => (
          <div key={p.key} data-testid={`admin-plan-card-${p.key}`}
                className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Plan</div>
                <div className="font-display text-xl font-bold capitalize mt-0.5">{p.name}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500">Price</div>
                <div className="text-sm font-display font-semibold">
                  {p.price_php_monthly === 0 ? "Free" : `₱${p.price_php_monthly.toLocaleString()}/mo`}
                </div>
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-600 space-y-0.5">
              <div>Staff: <strong>{p.staff_limit ?? "Unlimited"}</strong></div>
              <div>Fleet points: <strong>{p.fleet_points ?? "Unlimited"}</strong></div>
            </div>
            <div className="mt-3 rounded-lg bg-slate-50 p-2">
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Companies on this plan</div>
              <div className="font-display text-2xl font-bold">{p.companies_on_plan}</div>
              <div className="text-[11px] text-slate-600 mt-0.5">
                {p.active} active · {p.trial} on trial
              </div>
            </div>
            {p.summary && <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">{p.summary}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}


export function AdminSupport() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/recent-activity").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  return (
    <div data-testid="admin-support-page">
      <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
        <Headset className="w-5 h-5" /> Customer Support / Recent Activity
      </h1>
      <p className="text-sm text-slate-600 mt-2">
        Quick triage view: the latest users, businesses, outreach logs, and inquiries.
        Open the Users or Businesses pages to investigate or act on an account.
      </p>
      <div className="mt-5 grid lg:grid-cols-2 gap-4">
        <Card icon={Users} title="New users" link="/admin/users" linkLabel="Manage users"
               data-testid="support-recent-users">
          {data.recent_users.map((u) => (
            <Row key={u.id}
                  left={<span className="font-medium">{u.email}</span>}
                  middle={<span className="text-xs text-slate-500">{u.account_type}</span>}
                  right={<span className="text-[11px] text-slate-500">{(u.created_at || "").slice(0, 10)}</span>} />
          ))}
        </Card>
        <Card icon={Building2} title="New businesses" link="/admin/businesses" linkLabel="Manage businesses"
               data-testid="support-recent-companies">
          {data.recent_companies.map((c) => (
            <Row key={c.id}
                  left={<span className="font-medium">{c.name}</span>}
                  middle={<span className="text-xs text-slate-500">{c.city}</span>}
                  right={<span className="text-[11px] text-slate-500">{(c.created_at || "").slice(0, 10)}</span>} />
          ))}
        </Card>
        <Card icon={ClipboardList} title="Latest outreach logs" link="/admin/outreach-logs" linkLabel="See all logs"
               data-testid="support-recent-logs">
          {data.recent_outreach_logs.map((l) => (
            <Row key={l.id}
                  left={<span className="font-medium">{l.business_name}</span>}
                  middle={<span className="text-xs text-slate-500">{l.result_status}</span>}
                  right={<span className="text-[11px] text-slate-500">{l.promoter_name}</span>} />
          ))}
          {data.recent_outreach_logs.length === 0 &&
            <div className="text-center text-slate-500 text-sm py-4">No logs yet.</div>}
        </Card>
        <Card icon={Bell} title="Latest booking inquiries" link="/admin/businesses" linkLabel="-"
               data-testid="support-recent-inquiries">
          {data.recent_inquiries.map((i) => (
            <Row key={i.id}
                  left={<span className="font-medium">{i.business_name || i.company_id}</span>}
                  middle={<span className="text-xs text-slate-500">{i.customer_name}</span>}
                  right={<span className="text-[11px] text-slate-500">{(i.created_at || "").slice(0, 10)}</span>} />
          ))}
          {data.recent_inquiries.length === 0 &&
            <div className="text-center text-slate-500 text-sm py-4">No inquiries yet.</div>}
        </Card>
      </div>
    </div>
  );
}


function Card({ icon: Icon, title, link, linkLabel, children, "data-testid": testId }) {
  return (
    <section data-testid={testId} className="rounded-2xl border border-slate-200 bg-white p-4">
      <header className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-500" />
          <h2 className="font-display font-semibold">{title}</h2>
        </div>
        {link && linkLabel && linkLabel !== "-" && (
          <Link to={link} className="text-xs text-slate-500 hover:text-slate-800 underline">{linkLabel}</Link>
        )}
      </header>
      <div className="divide-y divide-slate-100 text-sm">{children}</div>
    </section>
  );
}


function Row({ left, middle, right }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5">
      <div className="min-w-0 flex-1 truncate">{left}</div>
      <div className="hidden sm:block">{middle}</div>
      <div>{right}</div>
    </div>
  );
}
